import * as React from "react";
import NumberFlow from "@number-flow/react";
import { InfinityIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { FramePanel } from "@/components/ui/frame";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

/**
 * A grid of Stats in one panel (usage and funds dialogs).
 *
 * @example
 * import { Frame, FrameHeader, FrameTitle } from "@/components/ui/frame"
 * import { Stat, StatGrid } from "@/components/ui/stat"
 *
 * const USD = { style: "currency", currency: "USD" } as const
 *
 * export function Funds({ available, spent, reserved }: { available: number; spent: number; reserved: number }) {
 *   return (
 *     <Frame>
 *       <FrameHeader>
 *         <FrameTitle>Funds</FrameTitle>
 *       </FrameHeader>
 *       <StatGrid>
 *         <Stat label="Available" value={available} format={USD} highlight />
 *         <Stat label="Spent" value={spent} format={USD} />
 *         <Stat label="Reserved" value={reserved} format={USD} tooltip="Agreed with creators, not paid out yet." />
 *       </StatGrid>
 *     </Frame>
 *   )
 * }
 */
function StatGrid({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <FramePanel
      data-slot="stat-grid"
      className={cn("grid grid-cols-2 gap-4 md:grid-cols-3", className)}
      {...props}
    />
  );
}

/** Label over an animated figure; a tooltip explains how it is counted. */
function Stat({
  label,
  value,
  highlight,
  format,
  tooltip,
}: {
  label: string;
  value: number | "unlimited";
  /** The headline figure of the grid: one size up. */
  highlight?: boolean;
  format?: React.ComponentProps<typeof NumberFlow>["format"];
  tooltip?: string;
}) {
  const cell = (
    <div
      data-slot="stat"
      className={cn("flex flex-col gap-0.5", tooltip && "cursor-help hover:opacity-60")}
    >
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className={cn("font-mono font-medium", highlight ? "text-base" : "text-sm")}>
        {value === "unlimited" ? (
          <InfinityIcon className="size-4" strokeWidth={3} />
        ) : (
          <NumberFlow value={value} format={format} />
        )}
      </span>
    </div>
  );
  if (!tooltip) return cell;
  return (
    <Tooltip delayDuration={200}>
      <TooltipTrigger asChild>{cell}</TooltipTrigger>
      <TooltipContent className="max-w-56 text-wrap">{tooltip}</TooltipContent>
    </Tooltip>
  );
}

export { Stat, StatGrid };
